import { httpFetch } from '../http';
import { BPR_FIRMWARE_BASE_S3_URL, BPR_FIRMWARE_ENVIRONMENT } from '../utils/constants';
import { _logMessage } from './robot-service-helpers';

const DEPLOYMENT_URL = `${BPR_FIRMWARE_BASE_S3_URL}bps-${BPR_FIRMWARE_ENVIRONMENT}-deployment.json`;

const ASSET_CONFIG_STORAGE_KEY = `bps-fwu-asset-config-cache-${BPR_FIRMWARE_ENVIRONMENT}`;

/** Deployment/asset config from the server (e.g. bps-staging-deployment.json). */
export interface AssetConfig {
  generated: string;
  [key: string]: unknown;
}

/** Cached asset config and validation headers for conditional requests (ETag / Last-Modified). */
interface AssetConfigCache {
  config: AssetConfig;
  etag: string | null;
  lastModified: string | null;
}

let assetConfigCache: AssetConfigCache | null = null;

function loadAssetConfigFromStorage(): void {
  try {
    const stored = localStorage.getItem(ASSET_CONFIG_STORAGE_KEY);
    if (stored) {
      assetConfigCache = JSON.parse(stored) as AssetConfigCache;
    }
  } catch {
    // Ignore parse or storage errors
  }
}

function saveAssetConfigToStorage(): void {
  if (!assetConfigCache) return;
  try {
    localStorage.setItem(ASSET_CONFIG_STORAGE_KEY, JSON.stringify(assetConfigCache));
  } catch {
    // Ignore quota or storage errors
  }
}

loadAssetConfigFromStorage();

/**
 * Fetches the asset config from the deployment URL, using cache for conditional requests
 * and returning a persisted copy when offline or when the fetch fails.
 */
export async function getAssetConfig(): Promise<AssetConfig> {
  try {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (assetConfigCache) {
      if (assetConfigCache.etag) {
        headers['If-None-Match'] = assetConfigCache.etag;
      }
      if (assetConfigCache.lastModified) {
        headers['If-Modified-Since'] = assetConfigCache.lastModified;
      }
    }

    const response = await httpFetch(DEPLOYMENT_URL, {
      method: 'GET',
      headers,
      responseType: 'json',
    });

    if (response.status === 304 && assetConfigCache?.config) {
      return assetConfigCache.config;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch asset config from ${DEPLOYMENT_URL}: ${response.status} ${response.statusText}`);
    }

    const etag = response.headers.get('etag') ?? null;
    const lastModified = response.headers.get('last-modified') ?? null;
    const config = (await response.json()) as AssetConfig;

    assetConfigCache = { config, etag, lastModified };
    saveAssetConfigToStorage();

    return config;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (assetConfigCache?.config) {
      _logMessage('Using cached asset config (offline or fetch failed): ', message);
      return assetConfigCache.config;
    }
    _logMessage(`Unable to fetch asset config from ${DEPLOYMENT_URL}: `, message);
    throw new Error(`An error occurred fetching the asset config from ${DEPLOYMENT_URL}. Message = ${message}`);
  }
}

/**
 * Preload the asset config at app start so a copy is always available, including when offline.
 * Call this when the app starts; it will fetch the config and persist it to localStorage.
 */
export async function preloadAssetConfig(): Promise<AssetConfig> {
  return getAssetConfig();
}
